import { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

/**
 * Custom hook for managing push notifications with controlled opt-in strategy
 * UK GDPR compliant: explicit consent, easy unsubscribe, consent timestamp stored
 */
export function usePushNotifications() {
  const [status, setStatus] = useState({
    isSupported: false,
    isEligible: false,
    hasConsented: false,
    consentDenied: false,
    isSubscribed: false,
    permission: 'default',
    loading: true,
    error: null
  });
  
  const [showConsentModal, setShowConsentModal] = useState(false);
  const hasCheckedEligibility = useRef(false);

  // Check if push notifications are supported
  const checkSupport = useCallback(() => {
    return 'serviceWorker' in navigator && 
           'PushManager' in window && 
           'Notification' in window;
  }, []);

  // Fetch push status from backend
  const fetchPushStatus = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/push/status`, { withCredentials: true });
      const data = response.data;
      
      setStatus(prev => ({
        ...prev,
        isEligible: data.is_eligible,
        hasConsented: data.has_consented,
        consentDenied: data.consent_denied,
        isSubscribed: data.is_subscribed,
        permission: Notification.permission,
        loading: false
      }));
      
      return data;
    } catch (error) {
      console.error('Failed to fetch push status:', error);
      setStatus(prev => ({ ...prev, loading: false, error: 'Failed to load status' }));
      return null;
    }
  }, []);

  // Track engagement for eligibility
  const trackEngagement = useCallback(async (type = 'dashboard_visit') => {
    try {
      await axios.post(`${API}/push/track-engagement?engagement_type=${type}`, {}, { withCredentials: true });
      
      // Check eligibility after tracking
      if (!hasCheckedEligibility.current) {
        hasCheckedEligibility.current = true;
        const statusData = await fetchPushStatus();
        
        // Show consent modal if eligible and haven't asked before
        if (statusData?.is_eligible && !statusData?.has_consented && !statusData?.consent_denied) {
          setShowConsentModal(true);
        }
      }
    } catch (error) {
      console.error('Failed to track engagement:', error);
    }
  }, [fetchPushStatus]);

  // Register service worker
  const registerServiceWorker = useCallback(async () => {
    if (!('serviceWorker' in navigator)) return null;
    
    try {
      const registration = await navigator.serviceWorker.register('/sw.js');
      console.log('Service Worker registered:', registration);
      return registration;
    } catch (error) {
      console.error('Service Worker registration failed:', error);
      return null;
    }
  }, []);

  // Subscribe to push notifications
  const subscribeToPush = useCallback(async () => {
    try {
      // Get VAPID public key
      const keyResponse = await axios.get(`${API}/push/vapid-key`, { withCredentials: true });
      const vapidPublicKey = keyResponse.data.publicKey;
      
      if (!vapidPublicKey) {
        throw new Error('VAPID key not configured');
      }
      
      // Register service worker
      const registration = await registerServiceWorker();
      if (!registration) {
        throw new Error('Service worker not available');
      }
      
      // Subscribe to push
      const subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(vapidPublicKey)
      });
      
      return subscription.toJSON();
    } catch (error) {
      console.error('Push subscription failed:', error);
      return null;
    }
  }, [registerServiceWorker]);

  // Handle user consent
  const handleConsent = useCallback(async (userConsented) => {
    setShowConsentModal(false);
    
    if (userConsented) {
      // User clicked "Yes" - request browser permission
      const permission = await Notification.requestPermission();
      
      if (permission === 'granted') {
        // Subscribe and send to backend
        const subscription = await subscribeToPush();
        
        await axios.post(`${API}/push/consent`, {
          consent_given: true,
          subscription: subscription
        }, { withCredentials: true });
        
        setStatus(prev => ({
          ...prev,
          hasConsented: true,
          isSubscribed: !!subscription,
          permission: 'granted'
        }));
      } else {
        // Browser permission denied - record in backend
        await axios.post(`${API}/push/consent`, {
          consent_given: false
        }, { withCredentials: true });
        
        setStatus(prev => ({
          ...prev,
          consentDenied: true,
          permission: permission
        }));
      }
    } else {
      // User clicked "No" in custom modal - never auto-prompt again
      await axios.post(`${API}/push/consent`, {
        consent_given: false
      }, { withCredentials: true });
      
      setStatus(prev => ({
        ...prev,
        consentDenied: true
      }));
    }
  }, [subscribeToPush]);

  // Unsubscribe from push notifications
  const unsubscribe = useCallback(async () => {
    try {
      await axios.delete(`${API}/push/unsubscribe`, { withCredentials: true });
      
      // Unsubscribe from service worker
      if ('serviceWorker' in navigator) {
        const registration = await navigator.serviceWorker.ready;
        const subscription = await registration.pushManager.getSubscription();
        if (subscription) {
          await subscription.unsubscribe();
        }
      }
      
      setStatus(prev => ({
        ...prev,
        isSubscribed: false,
        hasConsented: false
      }));
      
      return true;
    } catch (error) {
      console.error('Unsubscribe failed:', error);
      return false;
    }
  }, []);

  // Update preferences
  const updatePreferences = useCallback(async (preferences) => {
    try {
      await axios.put(`${API}/push/preferences`, preferences, { withCredentials: true });
      return true;
    } catch (error) {
      console.error('Failed to update preferences:', error);
      return false;
    }
  }, []);

  // Initialize on mount
  useEffect(() => {
    const isSupported = checkSupport();
    setStatus(prev => ({ ...prev, isSupported }));
    
    if (isSupported) {
      fetchPushStatus();
    } else {
      setStatus(prev => ({ ...prev, loading: false }));
    }
  }, [checkSupport, fetchPushStatus]);

  return {
    status,
    showConsentModal,
    setShowConsentModal,
    handleConsent,
    trackEngagement,
    unsubscribe,
    updatePreferences,
    fetchPushStatus
  };
}

// Helper function to convert VAPID key
function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - base64String.length % 4) % 4);
  const base64 = (base64String + padding)
    .replace(/-/g, '+')
    .replace(/_/g, '/');

  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);

  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

export default usePushNotifications;
